import { Capacitor } from '@capacitor/core';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../lib/useTranslation';
import { createCameraSession } from './cameraSession';
import { EVIDENCE_ACCEPT } from '../evidence/evidenceModel';
import { captureFileError, ODOMETER_FILE_ACCEPT, readCaptureFile, scaledCaptureImage } from './captureFile';
import ReceiptFilePreview from './ReceiptFilePreview';

interface Props {
  value: string;
  onChange: (image: string, fileName?: string) => void;
  fileName?: string;
  disabled?: boolean;
  kind: 'receipt' | 'odometer' | 'expense';
  variant?: 'desktop' | 'mobile';
  compact?: boolean;
}

function isNativeRuntime(): boolean {
  return Capacitor.isNativePlatform();
}

export default function CostImageCapture({ value, fileName, onChange, disabled, kind, variant = 'desktop', compact = false }: Props) {
  const t = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const uploadRef = useRef<HTMLInputElement>(null);
  const camera = useRef<ReturnType<typeof createCameraSession>>();
  if (!camera.current) camera.current = createCameraSession();
  const imageGeneration = useRef(0);
  const [cameraOn, setCameraOn] = useState(false);
  const [opening, setOpening] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const mobile = variant === 'mobile';

  const stopCamera = () => {
    camera.current!.stop();
    imageGeneration.current++;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraOn(false);
    setOpening(false);
  };

  useEffect(() => () => { camera.current!.stop(); imageGeneration.current++; }, []);

  const openCamera = async () => {
    setError('');
    const nativeRuntime = isNativeRuntime();
    if ((!nativeRuntime && !window.isSecureContext) || typeof navigator.mediaDevices?.getUserMedia !== 'function') {
      setError(t('costs.capture.cameraUnavailable'));
      return;
    }
    try {
      stopCamera();
      const request = imageGeneration.current;
      setOpening(true);
      const opened = await camera.current!.start(() => navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      }), async (stream) => {
        const video = videoRef.current;
        if (!video) throw new Error('CAMERA_CLOSED');
        video.srcObject = stream;
        await video.play();
      });
      if (request !== imageGeneration.current) return;
      setOpening(false);
      setCameraOn(opened);
    } catch (cameraError) {
      setOpening(false);
      if (videoRef.current) videoRef.current.srcObject = null;
      const name = cameraError instanceof DOMException ? cameraError.name : '';
      setError(t(name === 'NotAllowedError' || name === 'SecurityError'
        ? 'costs.capture.cameraDenied'
        : 'costs.capture.cameraUnavailable'));
    }
  };

  const takePhoto = async () => {
    const video = videoRef.current;
    if (!video?.videoWidth || !video.videoHeight) {
      setError(t('costs.capture.cameraNotReady'));
      return;
    }
    setBusy(true);
    setError('');
    const request = imageGeneration.current;
    try {
      const image = await scaledCaptureImage(video, video.videoWidth, video.videoHeight);
      if (request !== imageGeneration.current) return;
      onChange(image);
      setBusy(false);
      stopCamera();
    } catch {
      if (request === imageGeneration.current) setError(t('costs.capture.imageFailed'));
    } finally {
      if (request === imageGeneration.current) setBusy(false);
    }
  };

  const retake = async () => {
    onChange('');
    await openCamera();
  };

  const selectFile = async (file?: File) => {
    if (!file || disabled || busy) return;
    const invalid = captureFileError(file, kind);
    if (invalid) { setError(t(invalid)); return; }
    stopCamera();
    const request = imageGeneration.current;
    setBusy(true); setError('');
    try {
      const data = await readCaptureFile(file, kind);
      if (request === imageGeneration.current) onChange(data, file.name);
    } catch (failure) {
      if (request === imageGeneration.current) setError(t(failure instanceof Error && failure.message.startsWith('native.capture.')
        ? failure.message : 'native.capture.imageFailed'));
    } finally {
      if (request === imageGeneration.current) setBusy(false);
    }
  };

  const primaryClass = mobile ? 'lm-btn lm-btn-primary' : 'cst-btn-primary';
  const secondaryClass = mobile ? 'lm-btn lm-btn-ghost' : 'cst-btn-ghost';

  return (
    <div className={mobile ? 'lm-cost-live-capture' : 'cst-image-capture'}>
      <div className={'cst-camera' + (mobile ? ' lm-cost-live-camera' : '') + (compact ? ' is-compact' : '') + (cameraOn ? ' is-live' : '')}>
        {value ? (
          kind !== 'odometer' ? <ReceiptFilePreview value={value} fileName={fileName} />
            : <img src={value} alt={t('costs.capture.previewAlt.' + kind)} />
        ) : (
          <video ref={videoRef} muted playsInline />
        )}
        {!value && !cameraOn && (
          <div className="cst-camera-empty">
            <span className="material-symbols-rounded" aria-hidden="true">{kind === 'expense' ? 'add_a_photo' : kind === 'receipt' ? 'receipt_long' : 'speed'}</span>
            <strong>{t('costs.capture.emptyTitle.' + kind)}</strong>
            <span>{t('costs.capture.emptyHint.' + kind)}</span>
          </div>
        )}
        {!value && cameraOn && <div className="cst-scan-guide" aria-hidden="true" />}
      </div>

      <input ref={uploadRef} type="file" accept={kind !== 'odometer' ? EVIDENCE_ACCEPT : ODOMETER_FILE_ACCEPT} hidden disabled={disabled || busy}
        onChange={(event) => { const file = event.currentTarget.files?.[0]; event.currentTarget.value = ''; void selectFile(file); }} />
      <div className={'cst-capture-actions' + (!cameraOn ? ' cst-receipt-actions' : '')}>
        {value ? (
          <button type="button" className={secondaryClass} disabled={disabled || busy || opening} data-voice-target={`${kind}-camera`} onClick={() => void retake()}>
            <span className="material-symbols-rounded" aria-hidden="true">photo_camera</span>{t('costs.capture.openCamera')}
          </button>
        ) : cameraOn ? (
          <>
            <button type="button" className={primaryClass} disabled={disabled || busy} onClick={takePhoto}>
              <span className="material-symbols-rounded">photo_camera</span>{t('costs.capture.takePhoto')}
            </button>
            <button type="button" className={secondaryClass} disabled={disabled || busy} onClick={stopCamera}>{t('common.cancel')}</button>
          </>
        ) : (
          <button type="button" className={primaryClass} disabled={disabled || busy || opening} data-voice-target={`${kind}-camera`} onClick={openCamera}>
            <span className="material-symbols-rounded">photo_camera</span>{t('costs.capture.openCamera')}
          </button>
        )}
        {!cameraOn && <button type="button" className={value ? secondaryClass : primaryClass} data-voice-target={`${kind}-upload`}
          disabled={disabled || busy || opening} onClick={() => uploadRef.current?.click()}>
          <span className="material-symbols-rounded" aria-hidden="true">upload_file</span>{t(value ? 'costs.capture.replaceFile' : 'costs.capture.uploadFile')}
        </button>}
      </div>
      {!cameraOn && <p className="cst-receipt-file-hint">{t(kind !== 'odometer' ? 'costs.capture.fileHint' : 'costs.capture.imageFileHint')}</p>}
      {opening && <div role="status">{t('common.loading')} <button type="button" className={secondaryClass} onClick={stopCamera}>{t('common.cancel')}</button></div>}
      {busy && <div className={mobile ? 'lm-cost-progress' : 'cst-inline-state'}><span className={mobile ? 'lm-cost-spinner' : 'cst-spinner'} />{t('costs.capture.preparing')}</div>}
      {error && <div className={mobile ? 'lm-error' : 'cst-alert cst-alert-error'} role="alert">{error}</div>}
    </div>
  );
}
